import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { saveOfferService } from '../../services';
import styles from './index.module.scss';
import {
    OFFER_HEADING,
    offerBody,
    ACCEPT_LABEL,
    DECLINE_LABEL,
    SMALL_PRINT,
    ACCEPTED_HEADING,
    acceptedBody,
    RESPOND_FAILED,
} from '../../pages/Goodbye/saveOfferPresentation';
import { CONTACT_LINE, HELP_EMAIL, HELP_PHONE } from '../../pages/Goodbye/exitReasonPresentation';

// The save offer screen. One screen, one answer, no second ask.
//
// It is only ever reached from the entry inside the account, after that studio has sent
// its cancellation request, because that request is what makes it eligible on the server
// (see findCancellationRequest in server/src/services/saveOfferService.js). The
// standalone goodbye page never gets here.
//
// Nothing on this screen takes a payment or touches Stripe. Accepting records the answer
// and emails the team, who apply the code by hand, which is what Andrew asked for.
const SaveOffer = ({ offer, onDeclined }) => {
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [accepted, setAccepted] = useState(false);
    const [notifyFailed, setNotifyFailed] = useState(null);

    const { discountPercent, renewalDate } = offer;

    const accept = async () => {
        setSending(true);
        setError(null);

        try {
            await saveOfferService.respond({ accepted: true });
            setAccepted(true);
        } catch (err) {
            const res = err?.response;

            // The server answers 502 with recorded: true when the studio's acceptance was
            // written down but the team could not be told to apply the code. This must NOT
            // fall through to "Thanks for staying", because that sentence promises a
            // discount nobody has been asked to apply, and the studio would then be
            // charged the full amount at renewal believing it was handled. It is the one
            // failure on this screen that costs a studio money, so it gets the server's
            // own message and our phone number instead.
            if (res?.status === 502 && res?.data?.recorded) {
                setNotifyFailed(res?.data?.message || RESPOND_FAILED);
                return;
            }

            // Anything else: nothing was recorded, so stay on the offer and let them try
            // again. Falling through to the closing copy here would quietly cost a studio
            // a discount it had just asked for.
            setError(res?.data?.message || RESPOND_FAILED);
        } finally {
            setSending(false);
        }
    };

    const decline = async () => {
        setSending(true);
        setError(null);

        try {
            await saveOfferService.respond({ accepted: false });
        } catch (err) {
            // Deliberately swallowed, and this is the one place on either screen where
            // that is right. A studio that has said no is leaving, and its cancellation
            // request was recorded and emailed to us before this screen ever appeared. If
            // writing the decline fails, the cost is one missing row in our own reporting.
            // The alternative, showing an error, would trap a studio that is trying to
            // leave on a page offering it money, which is the behaviour every one of these
            // flows is written to avoid.
        } finally {
            setSending(false);
            onDeclined();
        }
    };

    const contact = (
        <p className={styles.contact}>
            {CONTACT_LINE} <a href={`mailto:${HELP_EMAIL}`}>{HELP_EMAIL}</a> or {HELP_PHONE}.
        </p>
    );

    if (notifyFailed) {
        return (
            <div className={styles.offer}>
                <h2 className={styles.heading}>{ACCEPTED_HEADING}</h2>
                <p className={styles.warning} role="alert">{notifyFailed}</p>
                {contact}
            </div>
        );
    }

    if (accepted) {
        return (
            <div className={styles.offer}>
                <h2 className={styles.heading}>{ACCEPTED_HEADING}</h2>
                <p className={styles.body}>{acceptedBody(discountPercent)}</p>
                {contact}
            </div>
        );
    }

    return (
        <div className={styles.offer}>
            <h2 className={styles.heading}>{OFFER_HEADING(discountPercent)}</h2>
            <p className={styles.body}>{offerBody({ discountPercent, renewalDate })}</p>

            {error ? <p className={styles.warning} role="alert">{error}</p> : null}

            <div className={styles.actions}>
                <Button variant="primary" onClick={accept} disabled={sending}>
                    {sending ? 'Sending...' : ACCEPT_LABEL}
                </Button>

                {/* Leaving is a link, not a second primary button. It is not hidden and it
                    is not harder to reach than the discount, but a studio that has already
                    told us it is going should not have to beat two buttons to finish. */}
                <Button variant="link" onClick={decline} disabled={sending}>
                    {DECLINE_LABEL}
                </Button>
            </div>

            <p className={styles.foot}>{SMALL_PRINT}</p>
        </div>
    );
};

export default SaveOffer;
